import React, { useState, useEffect } from "react";
import { Container, Row, Col } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { ActionButton, Buttons } from "../../components/Buttons";
import { DropDown } from "../../components/Forms";
import { MdOutlineDelete } from "react-icons/md";
import { LiaEditSolid } from "react-icons/lia";
import { HiOutlineDotsVertical } from "react-icons/hi";
import PageTitle from "../../components/PageTitle";
import NotifyData from "../../components/NotifyData";
import Pagination from "../../components/Pagination";
import TableUI from "../../components/TableUI";
import { fetchCustomers, deleteCustomer } from "../../slice/CustomerSlice";

// Excel Export
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

const Customer = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { customers, areas } = useSelector((state) => state.customers);

  const userData = JSON.parse(localStorage.getItem("user")) || {};
  const currentUserId = userData.user_id;

  const [selectedCustomer, setSelectedCustomer] = useState("");
  const [selectedArea, setSelectedArea] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  useEffect(() => {
    dispatch(fetchCustomers(""));
  }, [dispatch]);

  // Handle area selection to filter customers in the customer dropdown
  const filteredCustomerOptions = selectedArea
    ? customers
        .filter((c) => c.area_id === selectedArea)
        .map((c) => ({ value: c.customer_id, label: c.name }))
    : customers.map((c) => ({ value: c.customer_id, label: c.name }));

  const handleCreate = () => {
    navigate("/customer/create");
  };

  const handleEdit = (customer) => {
    navigate(`/customer/edit/${customer.customer_id}`);
  };

  const handleDelete = async (customerId) => {
    if (!currentUserId) {
      NotifyData("Operation Failed: User not authenticated", "error");
      return;
    }

    try {
      const msg = await dispatch(
        deleteCustomer({ customerId, currentUserId })
      ).unwrap();
      NotifyData(msg, "success");
      dispatch(fetchCustomers(""));
    } catch (error) {
      NotifyData(error.message, "error");
    }
  };

  // Reset filters
  const handleResetFilters = () => {
    setSelectedArea("");
    setSelectedCustomer("");
    setCurrentPage(1);
  };

  // Filter customers based on selected customer and area
  const filteredCustomers =
    customers?.filter((customer) => {
      const matchesCustomer = selectedCustomer
        ? customer.customer_id === selectedCustomer
        : true;
      const matchesArea = selectedArea
        ? customer.area_id === selectedArea
        : true;
      return matchesCustomer && matchesArea;
    }) || [];

  // Pagination logic
  const totalRecords = filteredCustomers.length;
  const totalPages = Math.ceil(totalRecords / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const currentData = filteredCustomers.slice(
    startIndex,
    startIndex + itemsPerPage
  );

  // Table headers
  const headers = ["No", "Customer Name", "Customer_no", "Plan","Plan Amount","Box No"];

  // Table body
  const body = currentData.map((customer, index) => ({
    key: customer.customer_id,
    values: [
      startIndex + index + 1,
      customer.name,
      customer.customer_no,
      customer.plan_name,
       customer.plan_prize,
      customer.box_no,
      <ActionButton
        options={[
          {
            label: "Edit",
            icon: <LiaEditSolid />,
            onClick: () => handleEdit(customer),
          },
          {
            label: "Delete",
            icon: <MdOutlineDelete />,
            onClick: () => handleDelete(customer.customer_id),
          },
        ]}
        label={<HiOutlineDotsVertical />}
      />,
    ],
  }));

const handleExportExcel = () => {
    const exportData = filteredCustomers.map((customer, index) => ({
      "No": index + 1,
      "Customer Name": customer.name,
      "Customer_no": customer.customer_no,
      "Plan": customer.plan_name,
      "Plan Amount": Number(customer.plan_prize || 0),
      "Box No": customer.box_no,
      "Area": customer.area_name,
      "Phone": customer.phone,
      "Address": customer.address,
      "Staff Name": customer.staff_name,
      "Total Amount": Number(customer.total_due_amount || 0),
      "Total Collections Amount": Number(customer.total_collection_amount || 0),
      "Total Pending Amount": Number(customer.total_pending_amount || 0),
      "Paid This Month": Number(customer.paid_this_month || 0), 
    }));

    const totalDue = exportData.reduce((acc, curr) => acc + curr["Total Amount"], 0);
    const totalPaidThisMonth = exportData.reduce((acc, curr) => acc + curr["Paid This Month"], 0);

   
    const totalRow = {
      "No": "",
      "Customer Name": "",
      "Customer_no": "",
      "Plan": "",
      "Plan Amount": null,
      "Box No": "",
      "Area": "",
      "Phone": "",
      "Address": "",
      "Staff Name": "TOTAL",
      "Total Amount": totalDue,
      "Total Collections Amount": "",
      "Total Pending Amount": "",
      "Paid This Month": totalPaidThisMonth, 
    };

    // 4. Combine data
    const finalData = [...exportData, {}, totalRow]; 

    // 5. Generate Workbook
    const worksheet = XLSX.utils.json_to_sheet(finalData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Customer Report");

    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    const data = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });
    
    saveAs(data, `Customer_Report_${new Date().toLocaleDateString()}.xlsx`);
};
  return (
    <div id="main">
      <Container fluid>
        <Row>
          <Col xs="6" className="py-3">
            <PageTitle PageTitle="Customer" showButton={false} />
          </Col>
          <Col xs="12" className=" d-flex flex-wrap justify-content-end gap-2">
            <Buttons
              btnlabel="Add New"
              className="add-btn"
              onClick={handleCreate}
            />
            <Buttons
              btnlabel="Download Excel"
              className="add-btn"
              onClick={handleExportExcel}
            />
            <Buttons
              btnlabel="Reset Filters"
              className="can-btn"
              onClick={handleResetFilters}
            />
          </Col>

          {/* Customer and Area Dropdown Filters */}
          <Col xs="12" lg="3">
            <DropDown
              textlabel="Filter by Area"
              name="area_id"
              value={selectedArea}
              onChange={(e) => {
                setSelectedArea(e.target.value);
                setSelectedCustomer("");
                setCurrentPage(1);
              }}
              options={[
                { value: "", label: "All Areas" },
                ...areas.map((area) => ({
                  value: area.area_id,
                  label: area.area_name,
                })),
              ]}
            />
          </Col>
          <Col xs="12" lg="3">
            <DropDown
              textlabel="Filter by Customer"
              name="customer_id"
              value={selectedCustomer}
              onChange={(e) => {
                setSelectedCustomer(e.target.value);
                setCurrentPage(1);
              }}
              options={[
                { value: "", label: "All Customers" },
                ...filteredCustomerOptions,
              ]}
            />
          </Col>

          <Col xs="12" lg="6" className="d-flex justify-content-end">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </Col>
          {/* Table with TableUI */}
          <Col xs="12" className="py-3 tableui">
            <TableUI
              headers={headers}
              body={body}
              className="table"
              showActionColumn={true}
              noRecordMessage="No customers found"
            />
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default Customer;
